import { QueryDataState } from '@shared/interfaces';
import { DeliveryState, LowerRole, PaymentState } from '@shared/types';

import { Bugo } from '../bugo/bugo.interface';
import { BugoBrand } from '../bugoBrand/bugoBrand.interface';
import { Fevent } from '../fevent/fevent.interface';
import { Image } from '../media/media.interface';
import { Shop } from '../shop/shop.interface';
import { ShopItem, ShopItemInBugo } from '../shopItem/shopItem.interface';
import { ShopItemInfo } from '../shopItemInfo/shopItemInfo.interface';
import { User } from '../user/user.interface';
import { FuneralHomeInfo } from './../funeralHomeInfo/funeralHomeInfo.interface';

export enum ShopOrderPaymentProvider {
  Bootpay = 'Bootpay',
  Tosspayments = 'Tosspayments',
}

//* 토스 가상계좌 취소 시 함께 보내는 '환불받을 계좌'.
//* bank는 은행명을 그대로 보낸다 — 서버가 토스 코드로 변환한다(별칭·영문코드도 받음).
//* (스펙: docs/shop-order-vbank-cancel-spec.md)
export interface ShopOrderRefundReceiveAccount {
  bank: string;
  accountNumber: string;
  holderName: string;
}

//* ───── 면세계산서 발행 요청 · 수기환불(계좌이체) 요청 — seller 접수 → admin 처리 ─────
//* 상태머신: 없음 → Requested → Done | Rejected. 미요청 주문(구서버 포함)엔 필드 자체가
//* 없으므로 존재 판정은 order.taxInvoiceRequest?.status 식으로.
//* (스펙: docs/shop-order-seller-request-spec.md)

export type ShopOrderSellerRequestStatus = 'Requested' | 'Done' | 'Rejected';

//* requestedBy/processedBy — populate(select: info.name) 여부에 따라 _id 문자열 또는 축약 유저
export type ShopOrderRequestUser = string | { _id: string; info?: { name?: string } };

export interface ShopOrderTaxInvoiceRequest {
  status: ShopOrderSellerRequestStatus;
  bizRegImage?: string | Image; // 사업자등록증 사진 — populate 시 Image
  bizNumber?: string;
  email?: string; // 발행본 수신 이메일
  requestNote?: string;
  requestedBy: ShopOrderRequestUser;
  requestedAt: Date;
  cashReceiptCanceledAt?: Date; // admin이 현금영수증 취소 완료 체크한 시각
  processedBy?: ShopOrderRequestUser;
  processedAt?: Date;
  resultNote?: string; // 국세청 승인번호 / 반려 사유 등
}

export type ShopOrderManualRefundTarget = 'Customer' | 'SellerAdvance';

export interface ShopOrderManualRefund {
  status: ShopOrderSellerRequestStatus;
  account: { bank: string; accountNumber: string; holderName: string };
  target: ShopOrderManualRefundTarget; // SellerAdvance = 판매자 선지급 후 상환 이체
  amount: number;
  requestNote?: string;
  requestedBy: ShopOrderRequestUser;
  requestedAt: Date;
  processedBy?: ShopOrderRequestUser;
  processedAt?: Date;
  resultNote?: string; // 이체 메모 / 반려 사유 등
  //* Done 처리가 실제로 취소확정(Canceled+포인트환불)까지 수행했을 때만 true.
  //* 되돌리기가 이 마커를 보고 부수효과 역전 여부를 판단한다(도장만 찍힌 Done 오역전 방지).
  cancelFinalized?: boolean;
  //* 요청 접수 상태에서 토스 가상계좌 자동환불(취소 API + 환불계좌)로 처리된 건의 마커 —
  //* 서버가 취소 성공 시 Done과 함께 찍는다. 돈이 PG로 나갔으므로 되돌리기 대상이 아니다.
  //* (선배선: 구서버엔 없음 · docs/shop-order-vbank-cancel-spec.md §계좌환불 요청 건의 자동환불)
  refundedVia?: 'PgAuto';
}

//* ───── 현금영수증 자진발행 (PG를 안 거친 '계좌 입금' 건) ─────
//* PG 결제는 PG가 알아서 발행하지만 우리 계좌로 직접 받은 돈은 아무도 대신 발행해주지
//* 않아서 admin이 홈택스에 직접 친다. 시스템이 하는 일은 **실행이 아니라 목록과 도장**이다.
//* 필드(selfCashReceiptDetail) 부재 = 아직 아무도 손대지 않음 = 발행 대기 큐의 정의.
//* (스펙: docs/shop-order-cash-receipt-spec.md)

export enum ShopOrderCashReceiptStatus {
  Issued = 'Issued', // 홈택스 발행 완료
  CancelRequested = 'CancelRequested', // 취소 확정, 홈택스에선 아직 안 지움
  Canceled = 'Canceled', // 홈택스 취소 완료
}

export interface ShopOrderCashReceiptDetail {
  status: ShopOrderCashReceiptStatus;
  statusAt: Date; // 이 상태로 만든 시각
  //* populate(select: info.name) 여부에 따라 _id 문자열 또는 축약 유저
  statusBy?: ShopOrderRequestUser;
}

//* 발행액이 어디서 왔는지 — 홈택스에 칠 금액을 오해하지 않게 화면에 반드시 구분해 띄운다.
//*  - deposit    : 전화주문 입금건. 발행액 = 결제 전액
//*  - itemChange : PG 결제 건인데 상품교체 차액만 계좌로 받은 것. 발행액 = **차액만**
//*    (원결제분은 PG가 이미 발행했다 — paymentByCash를 치면 이중 발행이 된다)
export type ShopOrderCashReceiptSourceKind = 'deposit' | 'itemChange';

//* 처리 기한 구간 — 서버가 안전 5일 / 최대 30일 경계로 판정해 내려준다
export type ShopOrderCashReceiptTier = 'safe' | 'late' | 'over';

//* 두 큐(발행 대기·취소 대기)가 공유하는 행 모양 — 이 행만 보고 홈택스에 그대로 칠 수
//* 있게 서버가 조립해준다. 프론트도 표를 이 타입 위에서 공유한다.
export interface ShopOrderCashReceiptQueueItem {
  _id: string;
  orderNo: string; // _id 뒤 6자리 (프로젝트 전반의 표기 관례)
  kind: ShopOrderCashReceiptSourceKind;
  purchasedAt: Date;
  //*! 발행할 금액. itemChange면 **차액만**이고 결제총액이 아니다
  amount: number;
  paymentByCash: number; // 주문 결제 총액 — 교체분은 amount와 다르다 (대사용 참고값)
  paymentByPoint: number; // 적립금 사용분 — 현금이 아니라 발행 대상 아님
  paymentStatus: PaymentState;
  senderName?: string; // 주문자(보내신 분)
  senderPhoneNumber?: string; // 현금영수증 식별정보 (별도 필드 없이 주문자 번호를 그대로)
  itemName?: string;
  quantity?: number;
  hasItemChange: boolean;
  lastChangedAt?: Date; // 마지막 교체 시각 = 교체 차액의 실제 수취일
  paymentMemo?: string; // 수기 수취 경위 한 줄
  waitingDays: number; // 결제일로부터 지난 일수 (두 큐 모두 결제일 기준으로 통일됐다)
  //* 면세계산서 요청 상태 — 없으면 요청 자체가 없는 것. 계산서와 현금영수증은 한 주문에
  //* 공존할 수 없어 서버가 `Done`을 발행 대기 큐에서 빼므로, 여기 올 수 있는 값은
  //* `Requested`(지금 발행하면 곧 취소해야 한다 → 배지)와 `Rejected`뿐이다.
  taxInvoiceStatus?: ShopOrderSellerRequestStatus;
}

export interface ShopOrderCashReceiptPendingItem extends ShopOrderCashReceiptQueueItem {
  tier: ShopOrderCashReceiptTier;
  overdue: boolean; // tier === 'over' — 최대기한을 넘긴 건 (편의 필드)
}

export interface ShopOrderCashReceiptPendingResponse {
  //* "이 날짜 이전 건은 처리 완료로 간주" 안내용 하한선. 서버 상수라 화면에 박지 않는다
  cutoff: Date;
  range: { from: Date; to: Date; clamped: boolean };
  //* 기한 경계도 서버가 준다 — 프론트가 상수를 따로 들고 있다가 갈라지지 않게
  thresholds: { safeDays: number; limitDays: number };
  total: number; // 기간 전체 건수 (페이지 아님)
  totalAmount: number;
  //* 구간별 건수도 **기간 전체** 기준 — 페이지분만 세던 문제가 없다
  safeCount: number;
  lateCount: number;
  overCount: number;
  page: number;
  limit: number;
  byMonth: {
    month: string;
    count: number;
    amount: number;
    safe: number;
    late: number;
    over: number;
  }[];
  items: ShopOrderCashReceiptPendingItem[];
}

//* 취소 대기 = 주문 취소가 확정돼 서버가 Issued → CancelRequested로 자동 전환한 건.
//* 돈은 돌려줬는데 홈택스엔 매출이 남아있는 상태라, 사람이 홈택스에서 지우고 Canceled 도장.
export interface ShopOrderCashReceiptCancelPendingItem
  extends ShopOrderCashReceiptQueueItem {
  canceledAt?: Date; // 주문이 취소된 시각
  cancelReason?: string;
  requestedAt?: Date; // 취소 대기로 넘어온 시각 (= selfCashReceiptDetail.statusAt)
  //* 취소 실행자. **없으면 시스템(웹훅)이 자동으로 내린 것**이라는 뜻 — populate 안 된
  //* 원본 id라 이름은 못 띄운다. 사람/시스템 구분에만 쓴다.
  requestedBy?: string;
  //*! 대기일수 기준이 결제일이 아니라 **취소 대기로 넘어온 시각**이다. 발행 대기 큐의
  //*! waitingDays(결제일 기준)와 헷갈리지 않게 이름이 따로 있다.
  waitingDaysSinceRequest: number;
}

export interface ShopOrderCashReceiptCancelPendingResponse {
  total: number;
  totalAmount: number;
  page: number;
  limit: number; // 기본 100, 최대 200
  items: ShopOrderCashReceiptCancelPendingItem[];
}

export interface ShopOrderCashReceiptBulkResult {
  matchedCount: number;
  modifiedCount: number;
  //* 조건을 못 채워 건너뛴 건 — 조용히 삼키면 안 된다 (이미 같은 상태 / 대상 아님 /
  //* 취소 계열인데 우리 발행 기록이 없음 = PG 대행발행)
  skippedIds: string[];
}

//* 도장이 찍힌 주문 (신규 API 없이 기존 admin find + select). 위 큐 응답과 달리
//* 서버가 조립해주지 않으므로 select한 원본 필드 모양 그대로다.
export interface ShopOrderCashReceiptStampedRow {
  _id: string;
  selfCashReceiptDetail?: ShopOrderCashReceiptDetail;
  deliveryDetail?: {
    senderName?: string;
    senderPhoneNumber?: string;
  };
  orderDetail?: {
    shopItemEmbed?: { name?: string };
    quantity?: number;
    //* 발행액 파생에 필요 — PG 결제 건의 발행액은 결제총액이 아니라 이 차액의 합이다
    itemChanges?: { additionalPaymentByCash?: number; changedAt?: Date }[];
  };
  paymentDetail?: {
    status?: PaymentState;
    method?: string;
    purchasedAt?: Date;
    paymentByCash?: number;
    paymentByPoint?: number;
    canceledAt?: Date;
    cancelReason?: string;
  };
}

//* 소프트 삭제 상태 (백엔드 ShopOrderDocStatus와 동일)
export enum ShopOrderDocStatus {
  Active = 'Active',
  Deleted = 'Deleted',
}

export type ShopOrder<
  User = string,
  FuneralHomeInfo = string,
  Shop = string,
  Bugo = string,
  Fevent = string,
  DeliveredImage = string,
  ShopItem = string,
  BugoBrand = string,
> = {
  _id: string;
  user: User; // 구매자
  funeralHomeInfo: FuneralHomeInfo; // 장례식장
  shop: Shop; // 꽃집
  bugo?: Bugo; // 모바일 부고장 (nullable)
  bugoBrand?: BugoBrand;
  fevent: Fevent;
  bugoDetail: {
    condolencePhrase: string; // ex) "삼가 고인의 명복을 빕니다."
    senderPhrase: string; // ex) "서울대 전기공학부 일동"
  };
  hiddenBugo?: boolean;
  deliveryDetail: {
    status: DeliveryState; // enum: BeforePreparing, Preparing, Deliverying, Delivered
    deliveredAt?: Date;
    estimatedDeliveredAt: Date;
    senderAddress: string;
    senderName: string;
    senderPhoneNumber: string;
    receiverAddress: string; // funeralHomeInfo.address, (funeralHomeInfo.name, roomInfo?.name ?? fevent.roomInfoEmbed?.name)
    receiverPostalCode: string; // 수령인 주소 우편번호 funeralHomeInfo.postalCode
    receiverName: string; //bugo.member.fullname
    receiverPhoneNumber: string; //bugo.member.phoneNumber
    deliveredImage?: DeliveredImage;
  };
  orderDetail: {
    shopItem: ShopItem;
    shopItemEmbed: {
      name: string;
      priceRetail: number; // 실제 구매가
      priceRetailShow: number; // 표시용 정가
      priceRebate: number; // 리베이트가
      priceRebateWorker: number; //상조직원
      priceRebateAgency: number; //상조본사
      priceWhole: number; // 납품가
    };
    quantity: number;
    //* 상품교체 이력 — 차액을 계좌로 받은 건은 그 차액이 우리 자진발행 대상이다
    //* (발행액 파생: docs/shop-order-cash-receipt-spec.md ②)
    //* 하향 교체(토스 부분취소)는 additionalPaymentByCash=0 + pgCancel* 필드로 기록된다
    //* (docs/shop-order-change-item-refund-spec.md)
    itemChanges?: {
      previousShopItemEmbed?: { name?: string; priceRetail?: number } | null;
      additionalPaymentByCash?: number;
      //* 토스 부분취소로 돌려준 차액 — 상향 교체엔 없다
      pgCancelAmount?: number;
      pgBalanceAmount?: number; // 취소 후 토스 잔액
      pgCancelKey?: string | null;
      changedAt?: Date;
      memo?: string;
    }[];
  };
  paymentDetail: {
    provider: ShopOrderPaymentProvider;
    status: PaymentState; // enum: NotPaid(결제 자체를 안했음), Waiting (가상계좌 발급완료 후 입금 대기중), Paid (카드든, 가상계좌든 결제가 완료), Confirmed (구매 확정), Canceled (결제 완료후 취소)
    method: string;
    receiptId: string; // bootpay 결제여부 검증을 위한 key
    receiptUrl: string; // 사용자가 확인 가능한 영수증을 나타내는 url (접속가능)
    cancelId: string; // bootpay cancel시 id
    paymentByCash: number; // 현금으로 결제한 양
    paymentByPoint: number; // 포인트로 결제한 양
    isAccounted?: boolean; // 우리가 꽃집에 정산해줬는지 유무
    accountedDate?: Date;
    isRebated?: boolean; // 우리가 장례식장 또는 상조에 리베이트 줬는지 유무
    rebatedDate?: Date;
    requestedAt?: Date; // 주문을 한 날짜
    purchasedAt?: Date; // 결제를 한 날짜 (bootpay dto.purchased_at)
    canceledAt?: Date; // 결제를 취소된 날짜 (bootpay dto.revoked_at)
    cancelReason?: string; // 결제취소한 이유
    //* 운영 메모 — 상품교체·PG 부분취소 등이 한 줄씩 누적된다(서버가 append)
    memo?: string;
    isAbnormalPayment?: boolean; // 이상한 결제 여부 (paymentByCash != bootpay data.price 인 경우에 자동 취소시 true)
    vBank?: string;
    //* ── PG 원본 결제수단 상세 (백엔드 저장 대기 — 구서버는 통째로 부재) ──────────
    //* `method`는 '간편결제' 하나뿐이라 카드로 냈는지 머니·포인트로 냈는지 구분이 안 된다.
    //* 실제로 갈리는 건 `pgCard`의 유무다 — 카드면 매출전표, 없으면 잔액 결제라 간편결제사가
    //* 현금영수증을 발행한다(=면세계산서 요청 시 그 현금영수증을 먼저 취소해야 하는 건).
    //* 판정은 shopOrder.utils의 shopOrderCashReceiptKind()로만 할 것.
    //*! 접두사 pg = 발행 주체가 PG라는 뜻. 우리 자진발행 도장은 최상위 selfCashReceiptDetail.
    pgEasyPay?: {
      provider?: string; // '카카오페이' | '네이버페이' | '토스페이'
      amount?: number; // 간편결제 잔액(머니·포인트)으로 결제된 금액
      discountAmount?: number;
    } | null;
    pgCard?: {
      issuerCode?: string;
      cardType?: string; // '신용' | '체크' | '기프트'
    } | null;
    //* PG(토스)가 직접 발행한 현금영수증 — 가상계좌 건에 채워진다. 취소하려면 receiptKey가 필요.
    //*! 우리 자진발행분(selfCashReceiptDetail)과 **공존한다** — 가상계좌 주문에 교체 차액이
    //*! 붙으면 한 주문에 영수증이 둘이고 취소도 각각 해야 한다.
    pgCashReceipt?: {
      type?: string; // '소득공제' | '지출증빙' | '미발행'
      receiptKey?: string;
      receiptUrl?: string;
      amount?: number;
    } | null;
    //* 가상계좌 취소로 환불금이 나간 계좌 — 취소 요청 시점에만 기록된다.
    //* 토스가 조회 응답에 되돌려주지 않아 이 기록이 유일한 근거다 (CS "어디로 환불됐죠?" 대응).
    //* 가상계좌 취소가 아니면 필드 자체가 없다.
    cancelAccount?: ShopOrderRefundReceiveAccount;
  };
  taxInvoiceRequest?: ShopOrderTaxInvoiceRequest;
  manualRefund?: ShopOrderManualRefund;
  //* 우리가 홈택스에서 자진발행한 도장 (PG 발행분 paymentDetail.pgCashReceipt와 별개).
  //* 부재 = 아직 아무도 손대지 않음. (docs/shop-order-cash-receipt-spec.md)
  selfCashReceiptDetail?: ShopOrderCashReceiptDetail;
  //* 소프트 삭제 — Deleted는 목록에서 빠질 뿐 문서는 남는다.
  //* 레거시 주문엔 필드 자체가 없으므로 '있으면 Deleted인지'로만 판정할 것
  docStatus?: ShopOrderDocStatus;
  deletedAt?: Date;
  deleteReason?: string;
  createdAt: Date;
  updatedAt: Date;
};

//* 요청 모달·목록 행이 공통으로 쓰는 최소 주문 형태 — populate 정도가 달라도 수용
export interface ShopOrderWithSellerRequests {
  _id: string;
  paymentDetail: ShopOrder['paymentDetail'];
  orderDetail?: { shopItemEmbed?: { name?: string }; quantity?: number };
  //* admin 목록 조회에서만 populate — 프리셋 노출 조건(shop 이름) 판정에 사용
  shop?: string | { _id?: string; name?: string };
  taxInvoiceRequest?: ShopOrderTaxInvoiceRequest;
  manualRefund?: ShopOrderManualRefund;
  //* 우리 자진발행 도장 — 현금영수증 종류 판정(shopOrderCashReceiptKind)에 필요하다
  selfCashReceiptDetail?: ShopOrderCashReceiptDetail;
}

//* 요청 목록 페이지 행 — admin 조회 시 shop(name) populate
export type ShopOrderSellerRequestRow = Omit<ShopOrder, 'shop'> & {
  shop?: string | { _id: string; name?: string };
};

//* 부고문자에서 사용하는 타입
export type ShopOrderInBugo = ShopOrder<
  string,
  string,
  string,
  string,
  string,
  string,
  ShopItemInBugo
>;

export type ShopOrderListByBugo = ShopOrder<
  string,
  string,
  string,
  string,
  Fevent,
  string
>[];

export type ShopOrderListByBugoState = QueryDataState<ShopOrderListByBugo>;

export type ShopOrderForStore = ShopOrder<
  string,
  string,
  Shop,
  string,
  string,
  Image,
  ShopItem<ShopItemInfo<string, Image>, string, string, Image>
>;

export type ShopOrderForStoreState = QueryDataState<ShopOrderForStore>;

export type ShopOrderListForStore = ShopOrderForStore[];

export type ShopOrderForAgencyAdmin = ShopOrder<
  string,
  FuneralHomeInfo,
  string,
  string,
  Fevent<User>,
  Image
>;

export type ShopOrderListForStoreState = QueryDataState<ShopOrderListForStore>;

export type ShopOrderListForAgencyAdminState = QueryDataState<ShopOrderForAgencyAdmin[]>;

export type ShopOrderForSeller = ShopOrder<
  User,
  string,
  string,
  Bugo<string, Fevent, BugoBrand>,
  string,
  Image,
  ShopItem<ShopItemInfo<string, Image>, string, string, Image>
>;

//* 전화번호 검색 결과 — 상가(고인·장례식장) 표기를 위해 bugo.fevent까지 populate한 모양
export type ShopOrderByPhoneForSeller = ShopOrder<
  string,
  string,
  string,
  Bugo<string, Fevent<string, FuneralHomeInfo>, string, string>
>;

export type ShopOrderForSellerAccount = ShopOrder<
  string,
  FuneralHomeInfo,
  string,
  Bugo<User, string, BugoBrand>,
  Fevent
>;

export type ShopOrderForWorker = ShopOrder<
  string,
  FuneralHomeInfo,
  string,
  string,
  Fevent
>;

export enum ShopOrderType {
  Bugo = 'Bugo',
  ReturnGift = 'ReturnGift',
}

export type PaymentProvider = 'toss' | 'bootpay';

export interface ShopOrderCreateDto {
  user: string; // user._id
  shop: string; // shop._id
  type: ShopOrderType;
  bugo: string; // bugo._id
  bugoDetail: {
    condolencePhrase: string;
    senderPhrase: string;
  };
  deliveryDetail: {
    estimatedDeliveredAt: Date;
    senderAddress: string;
    senderName: string;
    senderPhoneNumber: string;
    receiverAddress: string;
    receiverPostalCode: string;
    receiverName: string;
    receiverPhoneNumber: string;
  };
  orderDetail: {
    shopItem: string; // shopItem._id
    quantity: number;
  };
  paymentDetail: {
    paymentByCash: number;
    paymentByPoint: number;
    provider?: PaymentProvider;
  };
}

export interface ShopOrderUpdate {
  _id: string;
  user?: User; // 구매자
  funeralHomeInfo?: string; // 장례식장
  shop?: string; // 꽃집
  bugo?: string; // 모바일 부고장 (nullable)
  fevent?: string;
  bugoDetail?: {
    condolencePhrase?: string; // ex) "삼가 고인의 명복을 빕니다."
    senderPhrase?: string; // ex) "서울대 전기공학부 일동"
  };
  deliveryDetail?: {
    status?: DeliveryState; // enum: BeforePreparing, Preparing, Deliverying, Delivered
    deliveredAt?: Date;
    estimatedDeliveredAt?: Date;
    senderAddress?: string;
    senderName?: string;
    senderPhoneNumber?: string;
    receiverAddress?: string; // funeralHomeInfo.address, (funeralHomeInfo.name, roomInfo?.name ?? fevent.roomInfoEmbed?.name)
    receiverPostalCode?: string; // 수령인 주소 우편번호 funeralHomeInfo.postalCode
    receiverName?: string; //bugo.member.fullname
    receiverPhoneNumber?: string; //bugo.member.phoneNumber
    deliveredImage?: string;
  };
  orderDetail?: {
    shopItem?: string;
    shopItemEmbed?: {
      name?: string;
      priceRetail?: number; // 실제 구매가
      priceRetailShow?: number; // 표시용 정가
      priceRebate?: number; // 리베이트가
      priceRebateAgency?: number; // 상조 리베이트가
      priceRebateWorker?: number; // 상조 직원 리베이트가
      priceWhole?: number; // 납품가
    };
    quantity?: number;
  };
  paymentDetail?: {
    status?: PaymentState; // enum: NotPaid(결제 자체를 안했음), Waiting (가상계좌 발급완료 후 입금 대기중), Paid (카드든, 가상계좌든 결제가 완료), Confirmed (구매 확정), Canceled (결제 완료후 취소)
    method?: string;
    receiptId?: string; // bootpay 결제여부 검증을 위한 key
    receiptUrl?: string; // 사용자가 확인 가능한 영수증을 나타내는 url (접속가능)
    cancelId?: string; // bootpay cancel시 id
    paymentByCash?: number; // 현금으로 결제한 양
    paymentByPoint?: number; // 포인트로 결제한 양
    isAccounted?: boolean; // 우리가 꽃집에 정산해줬는지 유무
    isRebated?: boolean; // 우리가 장례식장 또는 상조에 리베이트 줬는지 유무
    requestedAt?: Date; // 주문을 한 날짜
    purchasedAt?: Date; // 결제를 한 날짜 (bootpay dto.purchased_at)
    canceledAt?: Date; // 결제를 취소된 날짜 (bootpay dto.revoked_at)
    cancelReason?: string; // 결제취소한 이유
    //* 운영 메모 — 상품교체·PG 부분취소 등이 한 줄씩 누적된다(서버가 append)
    memo?: string;
    isAbnormalPayment?: boolean; // 이상한 결제 여부 (paymentByCash != bootpay data.price 인 경우에 자동 취소시 true)
  };
}

export interface ShopOrderUpdateDetailStoreCustomerDto {
  bugoDetail?: {
    condolencePhrase?: string;
    senderPhrase?: string;
  };
  deliveryDetail?: {
    senderAddress?: string;
    senderName?: string;
    senderPhoneNumber?: string;
    receiverAddress?: string;
    receiverPostalCode?: string;
    receiverName?: string;
    receiverPhoneNumber?: string;
  };
}

export interface ShopOrderCancel {
  _id: string;
  cancelReason: string;
  //* provider=Tosspayments + method='가상계좌' 건에만 필요(그 조합이 아니면 서버가 싣지 않는다).
  //* 없으면 서버가 토스를 부르기 전에 400으로 끊는다.
  refundReceiveAccount?: ShopOrderRefundReceiveAccount;
}

export interface ShopOrderCancelStoreCustomerDto {
  cancelReason: string;
}

export type StoreRole =
  | LowerRole.Admin
  | LowerRole.StoreCustomer
  | LowerRole.StoreGuest
  | LowerRole.Public;
